import {
  CartAndLineItemGroups,
  CartStates,
  CommerceToolsCart,
  LineItemGroup,
} from '@/types/commercetools';
import { productTypeQueryOptions } from '@/hooks/useProductType';
import { getQueryClient } from '@/components/QueryClient/getQueryClient';
import { AppError } from '@/utility/errorClasses';
import {
  getAppearanceId,
  getDecorationLineItem,
  getProductTypeId,
  parseConfigurationDetails,
} from '@/utility/lineItems/lineItemHelpers';
import { addPriceBreakdown, getLineItemTotalPrice } from '@/utility/lineItems/lineItemPrices';
import { Locale } from '@/utility/locale';
import { get } from '@/utility/requestHelpers/clientRequestHelpers';

export const fetchLineItems = async (
  cartId: string,
  locale: Locale
): Promise<CartAndLineItemGroups | null> => {
  let cart: CommerceToolsCart;

  try {
    cart = await get<CommerceToolsCart>(`/api/v1/carts/${cartId}`);
  } catch (error) {
    // IF cart is not found, we ignore the error and return null.
    if (error instanceof AppError && error.statusCode === 404) {
      return null;
    }
    throw error;
  }

  if (cart.cartState !== CartStates.ACTIVE) {
    return null;
  }

  const lineItemGroups: LineItemGroup[] = [];

  for (const lineItem of cart.lineItems) {
    if (!lineItem.productKey.startsWith('product')) {
      console.info('Skipping non-product line item');
      continue;
    }
    const productId = lineItem.custom.fields.sprdProductId;
    const productLookupId = lineItem.custom.fields.sprdProductLookupId;
    const shopId = lineItem.custom.fields.sprdShopId;
    const appearanceId = getAppearanceId(lineItem);
    const groupId = `${productId}-${appearanceId}`;
    const priceBreakdown = getLineItemTotalPrice(lineItem, cart);

    const match = lineItemGroups.find((group) => group.groupId === groupId);
    if (match) {
      match.lineItems.push(lineItem);
      match.groupQuantity = match.lineItems.reduce((acc, cur) => acc + cur.quantity, 0);
      match.groupTotalCentAmount = match.groupTotalCentAmount + priceBreakdown.totalPrice;
      match.priceBreakdown = addPriceBreakdown(match.priceBreakdown, priceBreakdown);
      continue;
    }

    const queryClient = getQueryClient();
    void queryClient.prefetchQuery(
      productTypeQueryOptions(shopId, getProductTypeId(lineItem), locale)
    );

    const lineItemGroup: LineItemGroup = {
      groupId,
      name: lineItem.name.en ?? '',
      sellableId: lineItem.custom.fields.sprdSellableId,
      productId,
      productLookupId,
      productTypeId: getProductTypeId(lineItem),
      appearanceId,
      shopId,
      groupQuantity: lineItem.quantity,
      groupTotalCentAmount: priceBreakdown.totalPrice,
      currencyCode: cart.totalPrice.currencyCode,
      priceBreakdown,
      lineItems: [lineItem],
      outOfStockItems: [],
      configurationDetails: parseConfigurationDetails(getDecorationLineItem(lineItem, cart)),
    };
    lineItemGroups.push(lineItemGroup);
  }

  return { cart, lineItemGroups };
};
